import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { useVenue } from './VenueProvider';
import { useAuth } from './AuthProvider';
import { Button } from './button';
import { Alert } from './alert';
import { logBitacora, BITACORA_ACTIONS } from '../utils/bitacora';
import { esSoloLectura } from '../utils/roles';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';

const ACCENT = "#1b4d2e";

const DEFAULT_METODOS = ["Efectivo", "Transferencia"];

const MetodosPagoManager = () => {
  const { selectedVenueId, metodosPago, metodosPagoLoading } = useVenue();
  const { userInfo, user } = useAuth();
  // Inversionista: ve los métodos, no los crea ni los borra (P-FRA-13).
  const soloLectura = esSoloLectura(userInfo);
  const [newMetodo, setNewMetodo] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [metodoToDelete, setMetodoToDelete] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loading = metodosPagoLoading;

  // Seed default métodos if collection is empty
  useEffect(() => {
    // El sembrado es una escritura: solo lo dispara quien puede escribir. La
    // tabla ya pinta los defaults aunque no estén sembrados.
    if (!selectedVenueId || loading || seeding || soloLectura) return;
    if (metodosPago.length > 0) return;
    const seed = async () => {
      setSeeding(true);
      // Double-check by reading directly (snapshot may not have arrived yet)
      const col = collection(db, 'venues', selectedVenueId, 'metodosPago');
      const snap = await getDocs(col);
      if (snap.empty) {
        for (const nombre of DEFAULT_METODOS) {
          await addDoc(col, { nombre });
        }
      }
      setSeeding(false);
    };
    seed();
  }, [selectedVenueId, loading, metodosPago.length, seeding, soloLectura]);

  const handleAddMetodo = async () => {
    if (!newMetodo.trim()) return;
    const exists = metodosPago.some(
      (m) => m.nombre.toLowerCase() === newMetodo.trim().toLowerCase()
    );
    if (exists) {
      setNewMetodo('');
      return;
    }
    const col = collection(db, 'venues', selectedVenueId, 'metodosPago');
    await addDoc(col, { nombre: newMetodo.trim() });
    logBitacora(selectedVenueId, {
      action: BITACORA_ACTIONS.METODO_PAGO_CREATE,
      entity: "metodo_pago",
      entityId: "",
      entityName: newMetodo.trim(),
      details: {},
    }, userInfo, user);
    setNewMetodo('');
  };

  const handleDeleteMetodo = async (id) => {
    const metodo = metodosPago.find((m) => m.id === id);
    if (metodo && DEFAULT_METODOS.some((d) => d.toLowerCase() === metodo.nombre.toLowerCase())) return;
    await deleteDoc(doc(db, 'venues', selectedVenueId, 'metodosPago', id));
    logBitacora(selectedVenueId, {
      action: BITACORA_ACTIONS.METODO_PAGO_DELETE,
      entity: "metodo_pago",
      entityId: id,
      entityName: metodo?.nombre || "",
      details: {},
    }, userInfo, user);
  };

  const confirmDelete = (id) => {
    setMetodoToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (metodoToDelete) {
      await handleDeleteMetodo(metodoToDelete);
    }
    setShowDeleteConfirm(false);
    setMetodoToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setMetodoToDelete(null);
  };

  // Merge defaults so they always appear, even before seeding
  const defaultNames = new Set(DEFAULT_METODOS.map((n) => n.toLowerCase()));
  const merged = [...metodosPago];
  for (const nombre of DEFAULT_METODOS) {
    if (!metodosPago.some((m) => m.nombre.toLowerCase() === nombre.toLowerCase())) {
      merged.push({ id: `_default_${nombre.toLowerCase()}`, nombre, _isDefault: true });
    }
  }
  const sorted = merged.sort((a, b) =>
    a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
  );

  const isDefaultMetodo = (nombre) => defaultNames.has(nombre.toLowerCase());

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Métodos de pago</h2>

      {/* Add form */}
      {!soloLectura && (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Nombre del método</label>
            <input
              type="text"
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              value={newMetodo}
              onChange={(e) => setNewMetodo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddMetodo(); }}
              placeholder="Ej: Cheque, Tarjeta de crédito..."
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleAddMetodo}
              className="flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Agregar
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        {loading || seeding ? (
          <div className="text-center py-12 text-zinc-400">Cargando métodos de pago...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            No hay métodos de pago registrados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700/60">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Método</th>
                  <th className="px-4 py-2.5 w-[80px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {sorted.map((m) => (
                  <tr key={m.id} className="group">
                    <td className="px-4 py-2.5 font-medium text-zinc-900 dark:text-white">{m.nombre}</td>
                    <td className="px-4 py-2.5 text-right">
                      {isDefaultMetodo(m.nombre) ? (
                        <span className="text-xs text-zinc-400 italic">Por defecto</span>
                      ) : soloLectura ? null : (
                        <button
                          type="button"
                          onClick={() => confirmDelete(m.id)}
                          className="text-zinc-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Alert open={showDeleteConfirm} onClose={handleCancelDelete}>
        <div className="font-semibold mb-2">
          ¿Estás seguro de que deseas eliminar este método de pago?
        </div>
        <div className="mb-4 text-sm text-zinc-600">
          Esta acción no se puede deshacer. Los pagos existentes conservarán el método registrado.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={handleCancelDelete}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default MetodosPagoManager;
